import type {
  ChecklistItem,
  ChecklistProgress,
  InsuranceChecklist,
  Post,
  Structure,
  StructureClaim,
} from '../types';
import {
  REPORT_SECTIONS,
  TOPIC_META,
  sectionForTopic,
  topicForItem,
  type ChecklistTopic,
  type ReportSectionKey,
} from '../constants/checklistTopics';
import { groupEvidenceByItem } from './checklistEvidence';
import { resolvePair, type ResolvedPair } from './beforeAfter';

/**
 * How strong the evidence behind one row is.
 *
 * The record must never read as more authoritative than what sits behind it.
 * A contractor's before-and-after pair and a homeowner ticking a box are both
 * real, but they are not the same claim, and an insurer reading this deserves
 * to see which one they are looking at. The lower rungs double as the list a
 * vendor can quote against.
 */
export type EvidenceRung = 'pair' | 'photo' | 'note' | 'attested' | 'open';

export const RUNG_LABEL: Record<EvidenceRung, string> = {
  pair: 'Documented before and after',
  photo: 'Photo on record',
  note: 'Noted, no photo',
  attested: 'Marked done, no photo yet',
  open: 'Not yet addressed',
};

/** Rungs that mean "nothing has actually been filed". Drives what is open. */
const UNDOCUMENTED: EvidenceRung[] = ['attested', 'open'];

export interface ReportRow {
  /** Topic id, or the raw checklist item id for untopiced work. */
  key: string;
  topic?: ChecklistTopic;
  /** The active model's wording where it has one, else the topic's own. */
  label: string;
  description?: string;
  rung: EvidenceRung;
  /** Every observation documenting this work, newest first. */
  posts: Post[];
  /** Before/after pairs among those posts, ready to render side by side. */
  pairs: ResolvedPair[];
  /** Posts already shown inside a pair, as either half. The view must not
   *  render these again on their own. */
  consumedByPair: Set<string>;
  /** True when the owner ticked this on the active model's checklist. */
  attested: boolean;
}

export interface ReportSection {
  key: ReportSectionKey;
  label: string;
  detail: string;
  rows: ReportRow[];
  documented: number;
}

export interface ModelCoverage {
  checklistId: string;
  name: string;
  hazard?: string;
  total: number;
  /** Items of that model this property has an observation for, via topic. */
  documented: number;
  isActive: boolean;
}

export interface TimelineEntry {
  post: Post;
  label?: string;
}

export interface ReportModel {
  structure: Structure;
  claim: StructureClaim | null;
  modelName: string;
  sections: ReportSection[];
  coverage: ModelCoverage[];
  openRows: ReportRow[];
  timeline: TimelineEntry[];
  /** Items ticked on the active model, over its item count. */
  attestedCount: number;
  attestedTotal: number;
  /** Rows carrying at least one observation, over the rows shown. */
  documentedCount: number;
  documentedTotal: number;
}

function createdAtMs(post: Post): number {
  const value = post.createdAt as { toMillis?: () => number } | undefined;
  return typeof value?.toMillis === 'function' ? value.toMillis() : 0;
}

function byNewest(a: Post, b: Post): number {
  return createdAtMs(b) - createdAtMs(a);
}

/**
 * Post ids a pair already displays, both halves.
 *
 * The "before" half is its own post doc and arrives in the evidence list like
 * any other, so without this it renders twice: once inside the pair, and again
 * below as a loose photo. The "after" half is the post that carries the pair.
 */
function consumedByPair(pairs: ResolvedPair[]): Set<string> {
  const ids = new Set<string>();
  for (const pair of pairs) {
    ids.add(pair.post.id);
    if (pair.post.beforePostId) ids.add(pair.post.beforePostId);
  }
  return ids;
}

function rungFor(posts: Post[], pairs: ResolvedPair[], attested: boolean): EvidenceRung {
  if (pairs.length > 0) return 'pair';
  if (posts.some((p) => p.imageUrl)) return 'photo';
  if (posts.length > 0) return 'note';
  return attested ? 'attested' : 'open';
}

/**
 * How many of a model's items this property has an observation for, resolved
 * through the shared topic so it works for a model the owner has never
 * opened. Deliberately counts evidence, not ticks: ticks live per
 * (structure, checklist), so a model that was never selected has no progress
 * doc at all and would otherwise always read zero.
 */
function coverageFor(
  checklist: InsuranceChecklist,
  postsByTopic: Map<string, Post[]>,
  postsByItem: Map<string, Post[]>
): number {
  let documented = 0;
  for (const item of checklist.items) {
    const topic = (item.topic as ChecklistTopic | undefined) ?? topicForItem(item.id);
    const hasTopicEvidence = topic ? (postsByTopic.get(topic)?.length ?? 0) > 0 : false;
    const hasOwnEvidence = (postsByItem.get(item.id)?.length ?? 0) > 0;
    if (hasTopicEvidence || hasOwnEvidence) documented++;
  }
  return documented;
}

/**
 * Assemble everything a printed readiness record renders.
 *
 * Rows are TOPICS, not checklist items. That is the decision the whole format
 * turns on: rows keyed by item make a document about a checklist, rows keyed
 * by topic make one about the property, and the same document then serves
 * every insurer.
 *
 * Coverage rule: the topics the active model asks for, plus any topic this
 * property has evidence for. So the record never invents an obligation the
 * owner's model does not carry, and never hides work they have already done.
 */
export function buildReportModel(input: {
  structure: Structure;
  claim: StructureClaim | null;
  checklist: InsuranceChecklist | null;
  progress: ChecklistProgress | null;
  allChecklists: InsuranceChecklist[];
  posts: Post[];
}): ReportModel {
  const { structure, claim, checklist, progress, allChecklists, posts } = input;

  const postsByItem = groupEvidenceByItem(posts);
  const postsByTopic = new Map<ChecklistTopic, Post[]>();
  for (const [itemId, itemPosts] of postsByItem) {
    const topic = topicForItem(itemId);
    if (!topic) continue;
    const bucket = postsByTopic.get(topic);
    if (bucket) bucket.push(...itemPosts);
    else postsByTopic.set(topic, [...itemPosts]);
  }

  const attestedIds = new Set((progress?.completedItems ?? []).map((c) => c.itemId));
  const activeItems = checklist?.items ?? [];

  // The active model's wording wins over the topic's generic label, so a FAIR
  // Plan owner reads FAIR Plan's phrasing for work they were asked to do.
  const itemByTopic = new Map<ChecklistTopic, ChecklistItem>();
  for (const item of activeItems) {
    const topic = (item.topic as ChecklistTopic | undefined) ?? topicForItem(item.id);
    if (topic && !itemByTopic.has(topic)) itemByTopic.set(topic, item);
  }

  const coveredTopics = new Set<ChecklistTopic>([...itemByTopic.keys(), ...postsByTopic.keys()]);

  const rows: ReportRow[] = [];

  for (const topic of coveredTopics) {
    const item = itemByTopic.get(topic);
    const topicPosts = [...(postsByTopic.get(topic) ?? [])].sort(byNewest);
    const pairs = topicPosts.map((p) => resolvePair(p)).filter((p): p is ResolvedPair => p != null);
    const attested = item ? attestedIds.has(item.id) : false;
    rows.push({
      key: topic,
      topic,
      label: item?.label ?? TOPIC_META[topic].label,
      description: item?.description,
      rung: rungFor(topicPosts, pairs, attested),
      posts: topicPosts,
      pairs,
      consumedByPair: consumedByPair(pairs),
      attested,
    });
  }

  // Untopiced work lands in Other under its own label: the active model's
  // untopiced items, plus any untopiced item the property has evidence for.
  // The second half matters because an observation filed against an item that
  // carries no topic and is not in the current model would otherwise vanish
  // from the record entirely, which is not honest about work someone did.
  // Agent-authored models carry no topics at all, so this is the whole report
  // for them, and behaves exactly as it did before topics existed.
  const untopicedIds = new Set<string>();
  for (const item of activeItems) {
    if (!((item.topic as ChecklistTopic | undefined) ?? topicForItem(item.id))) {
      untopicedIds.add(item.id);
    }
  }
  for (const itemId of postsByItem.keys()) {
    if (!topicForItem(itemId)) untopicedIds.add(itemId);
  }

  const activeById = new Map(activeItems.map((i) => [i.id, i]));
  for (const itemId of untopicedIds) {
    const item = activeById.get(itemId);
    const itemPosts = [...(postsByItem.get(itemId) ?? [])].sort(byNewest);
    const pairs = itemPosts.map((p) => resolvePair(p)).filter((p): p is ResolvedPair => p != null);
    const attested = attestedIds.has(itemId);
    // A post carries its item's label denormalized, which is the only name
    // available for an item belonging to some other model.
    const label =
      item?.label ??
      itemPosts.find((p) => p.checklistItemLabel)?.checklistItemLabel ??
      'Other work';
    rows.push({
      key: itemId,
      label,
      description: item?.description,
      rung: rungFor(itemPosts, pairs, attested),
      posts: itemPosts,
      pairs,
      consumedByPair: consumedByPair(pairs),
      attested,
    });
  }

  const sections: ReportSection[] = [];
  for (const spec of REPORT_SECTIONS) {
    const sectionRows = rows.filter((r) => sectionForTopic(r.topic) === spec.key);
    if (sectionRows.length === 0) continue;
    // Strongest evidence first, so the best of the property leads each section.
    const order: EvidenceRung[] = ['pair', 'photo', 'note', 'attested', 'open'];
    sectionRows.sort((a, b) => order.indexOf(a.rung) - order.indexOf(b.rung));
    sections.push({
      key: spec.key,
      label: spec.label,
      detail: spec.detail,
      rows: sectionRows,
      documented: sectionRows.filter((r) => !UNDOCUMENTED.includes(r.rung)).length,
    });
  }

  const activeId = checklist?.id;
  const coverage: ModelCoverage[] = allChecklists
    .filter((c) => c.stakeholderType === (claim?.stakeholderType ?? 'homeowner'))
    .map((c) => ({
      checklistId: c.id,
      name: c.displayName ?? c.insurerName,
      hazard: c.hazard,
      total: c.items.length,
      documented: coverageFor(c, postsByTopic, postsByItem),
      isActive: c.id === activeId,
    }))
    .sort((a, b) => {
      if (a.isActive !== b.isActive) return a.isActive ? -1 : 1;
      return b.documented / (b.total || 1) - a.documented / (a.total || 1);
    });

  const openRows = rows
    .filter((r) => UNDOCUMENTED.includes(r.rung))
    .sort((a, b) => a.label.localeCompare(b.label));

  const labelByPostId = new Map<string, string>();
  for (const row of rows) for (const p of row.posts) labelByPostId.set(p.id, row.label);
  const timeline: TimelineEntry[] = [...posts]
    .filter((p) => p.checklistItemId)
    .sort(byNewest)
    .map((post) => ({ post, label: labelByPostId.get(post.id) }));

  const shown = sections.flatMap((s) => s.rows);

  return {
    structure,
    claim,
    modelName: checklist?.displayName ?? checklist?.insurerName ?? 'REDI Baseline',
    sections,
    coverage,
    openRows,
    timeline,
    attestedCount: attestedIds.size,
    attestedTotal: activeItems.length,
    documentedCount: shown.filter((r) => !UNDOCUMENTED.includes(r.rung)).length,
    documentedTotal: shown.length,
  };
}
